import React, { useState } from 'react';
import PropTypes from 'prop-types';

import UTRModal from '../../../Modal';
import SavedListModalActions from '../../SavedListModalActions';
import clampToMaxCodeUnits from '../../../../utils/clampToMaxCodeUnits';
import { MAX_NOTE_LENGTH, NOTE_MODAL_TITLE, NOTE_PLACEHOLDER, CLEAR_NOTE_LABEL } from './constants';

import styles from './playerNoteModal.scss';

// The ticket's data-testid contract pins this exact literal (no `modal.` namespace), matching the
// sibling SaveToListModal / RemovePlayerConfirmModal — the web verifier's Playwright selectors depend
// on it. Intentional deviation from CLAUDE.md rule 4; don't "fix" it to the general convention.
const TESTID_PREFIX = 'add-note-modal';

// Add/Edit Player Note modal (185-11716): title + Clear Note link, a textarea pre-filled with the row's
// current note, and a centered Cancel/Save footer. Save is disabled until the textarea holds non-blank
// text (Story 11 AC); Clear always sends an empty note. The parent owns the write, the busy state, and
// the targeted row patch — this component only edits text and reports the chosen action.
const PlayerNoteModal = ({ initialNote, isSaving, onSave, onClear, onCancel }) => {
    const [text, setText] = useState(initialNote);

    const isSaveDisabled = text.trim().length === 0 || isSaving;

    const handleSave = () => {
        if (isSaveDisabled) {
            return;
        }
        // Persist the trimmed value so it matches the non-blank gate above — otherwise padded text
        // would save with its whitespace and come back padded on the next edit.
        onSave(text.trim());
    };

    return (
        <UTRModal
            title={NOTE_MODAL_TITLE}
            onCloseClick={onCancel}
            className={styles.playerNoteModal}
            data-testid={TESTID_PREFIX}
        >
            <div className={styles.header}>
                <div className={`${styles.desktopTitle} d-none d-md-block`} data-testid={`${TESTID_PREFIX}.desktop-title`}>
                    {NOTE_MODAL_TITLE}
                </div>
                <button
                    type="button"
                    className={styles.clearLink}
                    onClick={onClear}
                    disabled={isSaving || !initialNote}
                    data-testid={`${TESTID_PREFIX}.clear`}
                >
                    <i className={`far fa-times-circle ${styles.clearIcon}`} />
                    {CLEAR_NOTE_LABEL}
                </button>
            </div>
            <textarea
                className={styles.textarea}
                value={text}
                onChange={event => setText(clampToMaxCodeUnits(event.target.value, MAX_NOTE_LENGTH))}
                placeholder={NOTE_PLACEHOLDER}
                data-testid={`${TESTID_PREFIX}.textarea`}
            />
            {/*
              Code units, not whole characters like the name counter: the note API caps on
              note.Trim().Length (code units), so the reading must match what the server measures.
            */}
            <div className={styles.charCounter} data-testid={`${TESTID_PREFIX}.char-counter`}>
                {`${text.length}/${MAX_NOTE_LENGTH}`}
            </div>
            <SavedListModalActions
                data-testid={TESTID_PREFIX}
                onCancel={onCancel}
                onSave={handleSave}
                saveDisabled={isSaveDisabled}
                cancelTestidSuffix="cancel"
                primaryTestidSuffix="save"
            />
        </UTRModal>
    );
};

PlayerNoteModal.propTypes = {
    initialNote: PropTypes.string,
    isSaving: PropTypes.bool,
    onSave: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

PlayerNoteModal.defaultProps = {
    initialNote: '',
    isSaving: false,
};

export default PlayerNoteModal;
